import axios from 'axios';

// Use relative paths for Nginx reverse-proxy compatibility
const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? (process.env.REACT_APP_API_URL || 'http://localhost:8000/api')
  : '/api';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle responses and errors globally
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && error.config?.url !== '/auth/login') {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    if (error.response?.status === 403) {
      console.warn('Access Denied:', error.response?.data?.detail);
    }
    return Promise.reject(error);
  }
);

export const apiClient = axiosInstance;

// ============= AUTH SERVICES =============
export const authAPI = {
  changePassword: (data) => axiosInstance.post('/auth/change-password', data),
  requestPasswordReset: (data) => axiosInstance.post('/auth/password-reset-request', data),
  resetPassword: (data) => axiosInstance.post('/auth/password-reset-verify', data),
  login: (username, password) => 
    axiosInstance.post('/auth/login', { username, password }),
  getCurrentUser: () => 
    axiosInstance.get('/auth/me'),
  register: (data) => 
    axiosInstance.post('/auth/register', data),
  refresh: () => 
    axiosInstance.post('/auth/refresh'),
};

// ============= TRIPS SERVICES =============
export const tripAPI = {
  getAll: (filters = {}) => 
    axiosInstance.get('/trips', { params: filters }),
  getById: (tripId) => 
    axiosInstance.get(`/trips/${tripId}`),
  create: (data) => 
    axiosInstance.post('/trips', data),
  update: (tripId, data) => 
    axiosInstance.put(`/trips/${tripId}`, data),
  updateStatus: (tripId, newStatus) => 
    axiosInstance.patch(`/trips/${tripId}/status?new_status=${newStatus}`),
  delete: (tripId) => 
    axiosInstance.delete(`/trips/${tripId}`),
};

// ============= CONTAINERS SERVICES =============
export const containerAPI = {
  getAll: (filters = {}) => 
    axiosInstance.get('/containers', { params: filters }),
  getById: (containerId) => 
    axiosInstance.get(`/containers/${containerId}`),
  create: (data) => 
    axiosInstance.post('/containers', data),
  update: (containerId, data) =>
    axiosInstance.patch(`/containers/${containerId}`, data),
  select: (containerId) => 
    axiosInstance.post(`/containers/${containerId}/select`),
  updateStatus: (containerId, newStatus) => 
    axiosInstance.patch(`/containers/${containerId}/status?new_status=${newStatus}`),
  unlock: (containerId) => 
    axiosInstance.post(`/containers/${containerId}/unlock`),
  unassignDriver: (containerId) =>
    axiosInstance.post(`/containers/${containerId}/unlock`),
  getHistory: (containerId) => 
    axiosInstance.get(`/containers/${containerId}/history`),
};

// Alias for containers (plural)
export const containersAPI = containerAPI;

// ============= PORTS SERVICES =============
export const portAPI = {
  getAll: (filters = {}) => 
    axiosInstance.get('/ports', { params: filters }),
  getById: (portId) => 
    axiosInstance.get(`/ports/${portId}`),
  create: (data) => 
    axiosInstance.post('/ports', data),
  update: (portId, data) => 
    axiosInstance.put(`/ports/${portId}`, data),
  delete: (portId) => 
    axiosInstance.delete(`/ports/${portId}`),
};

// Alias for ports (plural)
export const portsAPI = portAPI;

// ============= DEPOTS SERVICES =============
export const depotsAPI = {
  getAll: (filters = {}) => 
    axiosInstance.get('/depots', { params: filters }),
  getPublic: (filters = {}) => 
    axiosInstance.get('/depots', { params: filters }),
  getById: (depotId) => 
    axiosInstance.get(`/depots/${depotId}`),
  create: (data) => 
    axiosInstance.post('/depots', data),
  update: (depotId, data) => 
    axiosInstance.put(`/depots/${depotId}`, data),
  delete: (depotId) => 
    axiosInstance.delete(`/depots/${depotId}`),
};

// ============= LOCATION TRACKING SERVICES =============
export const locationAPI = {
  submitLocation: (containerId, latitude, longitude, status = 'in_transit') => 
    axiosInstance.post(`/location-history?container_id=${containerId}`, {
      latitude,
      longitude,
      status,
    }),
  getContainerHistory: (containerId) => 
    axiosInstance.get(`/location-history/container/${containerId}`),
  getTripHistory: (tripId) => 
    axiosInstance.get(`/location-history/trip/${tripId}`),
};

// ============= REPORTS SERVICES =============
export const reportsAPI = {
  searchTrips: (identifier) => 
    axiosInstance.get(`/reports/trip/search?identifier=${identifier}`),
  searchContainers: (containerNumber) => 
    axiosInstance.get(`/reports/container/search?container_number=${containerNumber}`),
  getTripReport: (tripId) => 
    axiosInstance.get(`/reports/trip/${tripId}`),
  getContainerReport: (containerId) => 
    axiosInstance.get(`/reports/container/${containerId}`),
};

// ============= COMPANIES SERVICES =============
export const companiesAPI = {
  getAll: () => 
    axiosInstance.get('/companies'),
  getPublic: () => 
    axiosInstance.get('/companies'),
  getById: (id) => 
    axiosInstance.get(`/companies/${id}`),
  create: (data) => 
    axiosInstance.post('/companies', data),
  update: (id, data) => 
    axiosInstance.put(`/companies/${id}`, data),
  delete: (id) => 
    axiosInstance.delete(`/companies/${id}`),
};

// ============= USERS SERVICES =============
export const usersAPI = {
  getAll: () => 
    axiosInstance.get('/users'),
  getById: (id) => 
    axiosInstance.get(`/users/${id}`),
  create: (data) => 
    axiosInstance.post('/users', data),
  update: (id, data) => 
    axiosInstance.put(`/users/${id}`, data),
  delete: (id) => 
    axiosInstance.delete(`/users/${id}`),
};

// ============= AUDIT LOGS SERVICES =============
export const auditLogsAPI = {
  getAll: (filters = {}) => 
    axiosInstance.get('/audit-logs', { params: filters }),
  getByEntity: (entityType, entityId) => 
    axiosInstance.get(`/audit-logs?entity_type=${entityType}&entity_id=${entityId}`),
};
